import React from 'react';
import { Monitor, Check, Unlink } from 'lucide-react';
import { Promotion } from '../../../shared/types';

interface PromotionListProps {
    promotions: Promotion[];
    selectedIds: number[];
    onToggleSelection: (id: number) => void;
    onAddToScreen: (id: number) => void;
    onRemoveFromScreen?: (id: number) => void;
    isLinked: (id: number) => boolean;
    loading?: boolean;
}

export const PromotionList: React.FC<PromotionListProps> = ({
    promotions,
    selectedIds,
    onToggleSelection,
    onAddToScreen,
    onRemoveFromScreen,
    isLinked,
    loading = false
}) => {
    // Função para formatar números no padrão brasileiro
    const formatBrazilianNumber = (value: number | undefined, showCurrency: boolean = true): string => {
        const numValue = value ?? 0;
        const formattedNumber = numValue.toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
        return showCurrency ? `R$ ${formattedNumber}` : formattedNumber;
    };

    return (
        <div id="promotion-table-container" className="bg-white rounded-lg shadow-sm border overflow-hidden">
            <div className="overflow-x-auto">
                <table className="w-full min-w-[800px]">
                    <thead id="promotion-table-header" className="bg-gray-50 border-b">
                        <tr>
                            <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm font-medium text-gray-700 w-10 sm:w-12">
                                <span className="sr-only">Selecionar</span>
                            </th>
                            <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm font-medium text-gray-700 w-16 sm:w-20">ID</th>
                            <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm font-medium text-gray-700">Nome</th>
                            <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm font-medium text-gray-700">Categoria</th>
                            <th className="px-2 sm:px-4 py-2 sm:py-3 text-center text-xs sm:text-sm font-medium text-gray-700 w-14 sm:w-16">Promoção</th>
                            <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm font-medium text-gray-700">Status</th>
                            <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm font-medium text-gray-700">Preço</th>
                            <th className="px-2 sm:px-4 py-2 sm:py-3 text-left text-xs sm:text-sm font-medium text-gray-700">Unidade</th>
                            <th className="px-2 sm:px-4 py-2 sm:py-3 text-center text-xs sm:text-sm font-medium text-gray-700">Ações</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {loading ? (
                            <tr>
                                <td colSpan={9} className="px-2 sm:px-4 py-8 sm:py-12 text-center">
                                    <div className="flex flex-col items-center justify-center text-gray-500">
                                        <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-blue-500 mb-3 sm:mb-4"></div>
                                        <p className="text-base sm:text-lg font-medium">Verificando vinculação...</p>
                                        <p className="text-xs sm:text-sm mt-1">Aguarde enquanto verificamos os produtos vinculados</p>
                                    </div>
                                </td>
                            </tr>
                        ) : promotions.length === 0 ? (
                            <tr>
                                <td colSpan={9} className="px-2 sm:px-4 py-8 sm:py-12 text-center">
                                    <div className="flex flex-col items-center justify-center text-gray-500">
                                        <svg className="w-10 h-10 sm:w-12 sm:h-12 mb-3 sm:mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                                        </svg>
                                        <p className="text-base sm:text-lg font-medium">Nenhum produto encontrado</p>
                                        <p className="text-xs sm:text-sm mt-1">Ajuste os filtros de busca!</p>
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            promotions.map(promo => {
                                const linked = isLinked(promo.id);
                                const isSelected = selectedIds.includes(promo.id);
                                const isInPromotion = promo.originalPrice !== undefined && promo.originalPrice > 0;

                                return (
                                    <tr
                                        key={promo.id}
                                        className={`hover:bg-gray-50 transition-colors ${!linked ? 'opacity-75' : ''} ${isSelected ? 'bg-blue-50' : ''}`}
                                    >
                                        {/* Checkbox */}
                                        <td className="px-2 sm:px-4 py-2 sm:py-3">
                                            <button
                                                onClick={() => onToggleSelection(promo.id)}
                                                className={`w-4 h-4 sm:w-5 sm:h-5 rounded border-2 flex items-center justify-center transition-colors ${isSelected
                                                    ? 'bg-blue-500 border-blue-500 text-white'
                                                    : 'border-gray-400 bg-white hover:border-blue-500'
                                                    }`}
                                            >
                                                {isSelected && <Check size={12} className="sm:w-[14px] sm:h-[14px]" />}
                                            </button>
                                        </td>

                                        {/* ID */}
                                        <td className="px-2 sm:px-4 py-2 sm:py-3">
                                            <span className="text-xs text-gray-500 font-mono">{promo.id}</span>
                                        </td>

                                        {/* Nome */}
                                        <td className="px-2 sm:px-4 py-2 sm:py-3">
                                            <span className="text-xs sm:text-sm text-gray-900 font-medium">{promo.name}</span>
                                        </td>

                                        {/* Categoria */}
                                        <td className="px-2 sm:px-4 py-2 sm:py-3">
                                            <span className="text-xs text-gray-500 bg-gray-100 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded">{promo.category}</span>
                                        </td>

                                        {/* Promoção */}
                                        <td className="px-2 sm:px-4 py-2 sm:py-3 text-center">
                                            <div className="flex items-center justify-center">
                                                <div className={`w-4 h-4 sm:w-5 sm:h-5 rounded border-2 flex items-center justify-center ${isInPromotion
                                                    ? 'bg-green-500 border-green-500 text-white'
                                                    : 'border-gray-300 bg-gray-50'
                                                    }`}>
                                                    {isInPromotion && <Check size={10} className="sm:w-[12px] sm:h-[12px]" />}
                                                </div>
                                            </div>
                                        </td>

                                        {/* Status */}
                                        <td className="px-2 sm:px-4 py-2 sm:py-3">
                                            {linked ? (
                                                <span className="inline-flex items-center px-2 sm:px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                    Vinculado
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center px-2 sm:px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                                    Não vinculado
                                                </span>
                                            )}
                                        </td>

                                        {/* Preço */}
                                        <td className="px-2 sm:px-4 py-2 sm:py-3">
                                            <span className="text-xs sm:text-sm text-gray-900 font-semibold">
                                                {formatBrazilianNumber(promo.price)}
                                            </span>
                                            {promo.originalPrice && (
                                                <div className="text-xs text-gray-500 line-through">
                                                    {formatBrazilianNumber(promo.originalPrice)}
                                                </div>
                                            )}
                                        </td>

                                        {/* Unidade */}
                                        <td className="px-2 sm:px-4 py-2 sm:py-3">
                                            <span className="text-xs sm:text-sm text-gray-600">{promo.unit}</span>
                                        </td>

                                        {/* Ações - Botões alinhados ao centro */}
                                        <td className="px-2 sm:px-4 py-2 sm:py-3">
                                            <div className="flex justify-center items-center gap-1.5 sm:gap-2">
                                                <button
                                                    onClick={() => onAddToScreen(promo.id)}
                                                    className={`p-1.5 sm:p-2 rounded transition-colors ${linked
                                                        ? 'bg-green-500 text-white hover:bg-green-600'
                                                        : 'bg-yellow-500 text-white hover:bg-yellow-600'
                                                        }`}
                                                    title="Vincular à tela"
                                                >
                                                    <Monitor size={14} className="sm:w-[16px] sm:h-[16px]" />
                                                </button>
                                                {linked && onRemoveFromScreen && (
                                                    <button
                                                        onClick={() => onRemoveFromScreen(promo.id)}
                                                        className="bg-red-500 text-white p-1.5 sm:p-2 rounded hover:bg-red-600 transition-colors"
                                                        title="Desvincular da tela"
                                                    >
                                                        <Unlink size={14} className="sm:w-[16px] sm:h-[16px]" />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
